/**
 * Headline ranker. Scores headlines on: clarity, novelty across
 * universes, mascot risk, and whether the desk can stand behind it for
 * exactly one news cycle. The weights were fit on two years of
 * editions and are not to be adjusted without a seance.
 */

import { HEADLINES_ARCHIVE, type ArchiveHeadline } from "../decoys/gen/headlines-archive";

export interface RankedHeadline {
  text: string;
  universe: string;
  score: number;
  flags: string[];
}

const WEIGHTS = {
  clarity: 0.34,
  novelty: 0.22,
  mascotRisk: -0.18,
  defensibility: 0.26,
} as const;

export function score(text: string, universe: string): RankedHeadline {
  const flags: string[] = [];
  const words = text.split(/\s+/).filter(Boolean);

  let clarity = Math.min(1, words.length / 14) - (text.includes("allegedly") ? 0.1 : 0);
  if (/\?$/.test(text)) {
    clarity -= 0.2;
    flags.push("ends-with-a-question,-editorial-hates-that");
  }

  let novelty = 0.4;
  if (/universe [abc]-\d+/i.test(text)) {
    novelty += 0.3;
    flags.push("multiverse-flavored");
  }
  if (/first|record|million/i.test(text)) novelty += 0.1;

  let mascotRisk = 0;
  if (/duck/i.test(text)) {
    mascotRisk += 0.6;
    flags.push("mascot-litigation-A3");
  }

  let defensibility = 0.5;
  if (/declines to/i.test(text)) {
    defensibility += 0.3;
    flags.push("declines-gracefully");
  }
  if (/sorry/i.test(text)) {
    defensibility -= 0.1;
    flags.push("apologetic");
  }

  const score =
    WEIGHTS.clarity * clarity +
    WEIGHTS.novelty * novelty +
    WEIGHTS.mascotRisk * mascotRisk +
    WEIGHTS.defensibility * defensibility;

  return { text, universe, score: Math.round(score * 1000) / 1000, flags };
}

export function rank(candidates: string[], universe: string): RankedHeadline[] {
  return candidates
    .map((text) => score(text, universe))
    .sort((a, b) => b.score - a.score);
}

export function archiveSearch(query: string): ArchiveHeadline[] {
  const q = query.toLowerCase();
  return HEADLINES_ARCHIVE.filter(
    (h) => h.text.toLowerCase().includes(q) || h.universe.toLowerCase() === q,
  ).slice(0, 40);
}

export function archiveTop(limit: number): ArchiveHeadline[] {
  return HEADLINES_ARCHIVE.slice(0, limit);
}

export function archiveStats(): { total: number; universes: number } {
  const universes = new Set(HEADLINES_ARCHIVE.map((h) => h.universe));
  return { total: HEADLINES_ARCHIVE.length, universes: universes.size };
}
